import { useCallback, useEffect, useMemo, useState } from 'react';
import { getBackendUrl } from '../lib/apiUrls';
import { createWorkspaceClient } from '../lib/workspaceClient';

function formatDateKey(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function normalizeBriefing(briefing) {
  return {
    date: briefing?.date || formatDateKey(new Date()),
    mode: briefing?.mode || 'today',
    timezone: briefing?.timezone || null,
    generatedAt: briefing?.generatedAt || null,
    briefingMoment: briefing?.briefingMoment || 'morning',
    dayTone: briefing?.dayTone || 'normal',
    headline: briefing?.headline || 'Daily briefing',
    summary: briefing?.summary || '',
    passedToday: Array.isArray(briefing?.passedToday) ? briefing.passedToday : [],
    fixedSchedule: Array.isArray(briefing?.fixedSchedule) ? briefing.fixedSchedule : [],
    routinesToday: Array.isArray(briefing?.routinesToday) ? briefing.routinesToday : [],
    mustDoToday: Array.isArray(briefing?.mustDoToday) ? briefing.mustDoToday : [],
    overdue: Array.isArray(briefing?.overdue) ? briefing.overdue : [],
    alreadyDoneToday: Array.isArray(briefing?.alreadyDoneToday) ? briefing.alreadyDoneToday : [],
    goodSmallWins: Array.isArray(briefing?.goodSmallWins) ? briefing.goodSmallWins : [],
    tomorrowPrep: Array.isArray(briefing?.tomorrowPrep) ? briefing.tomorrowPrep : [],
    householdRadar: Array.isArray(briefing?.householdRadar) ? briefing.householdRadar : [],
    headsUp: Array.isArray(briefing?.headsUp) ? briefing.headsUp : [],
    range: briefing?.range || null,
    bestNextThing: briefing?.bestNextThing || null,
    debug: briefing?.debug || null,
  };
}

export function useDailyBriefing(currentUser) {
  const workspaceClient = useMemo(() => createWorkspaceClient(getBackendUrl()), []);
  const [briefing, setBriefing] = useState(null);
  const [filters, setFilters] = useState({
    date: formatDateKey(new Date()),
    scope: 'auto',
    mode: 'today',
  });
  const [status, setStatus] = useState({
    type: 'idle',
    message: 'Briefing has not loaded yet.',
  });
  const [isLoading, setIsLoading] = useState(false);

  const loadBriefing = useCallback(async (options = {}) => {
    if (!currentUser?.userId) {
      setBriefing(null);
      setStatus({ type: 'idle', message: 'Sign in to view the daily briefing.' });
      return;
    }

    setIsLoading(true);
    setStatus({ type: 'loading', message: 'Loading daily briefing...' });

    try {
      const payload = await workspaceClient.getDailyBriefing(currentUser.userId, filters);
      const nextBriefing = normalizeBriefing(payload?.briefing || payload);
      setBriefing(nextBriefing);
      setStatus({
        type: 'success',
        message: options.successMessage || 'Briefing loaded.',
      });
    } catch (error) {
      setStatus({
        type: 'error',
        message: error?.error || error?.message || 'Could not load daily briefing.',
      });
    } finally {
      setIsLoading(false);
    }
  }, [currentUser?.userId, filters, workspaceClient]);

  useEffect(() => {
    void loadBriefing();
  }, [loadBriefing]);

  const refreshBriefing = useCallback(async (options = {}) => {
    await loadBriefing(options);
  }, [loadBriefing]);

  const updateBriefingFilter = useCallback((field, value) => {
    setFilters((current) => ({ ...current, [field]: value }));
  }, []);

  const completeBriefingItem = useCallback(
    async (item) => {
      if (!currentUser?.userId || !item?.sourceId) {
        return false;
      }

      setIsLoading(true);
      setStatus({ type: 'loading', message: 'Completing item...' });

      try {
        const scope = item.scope === 'family' ? 'family' : 'personal';

        if (item.sourceType === 'task') {
          await workspaceClient.completeTask(currentUser.userId, item.sourceId, { scope });
        } else if (item.sourceType === 'reminder' && item.listId) {
          await workspaceClient.completeReminder(currentUser.userId, item.listId, item.sourceId, { scope });
        } else {
          return false;
        }

        await loadBriefing({ successMessage: 'Item completed.' });
        return true;
      } catch (error) {
        setStatus({
          type: 'error',
          message: error?.error || error?.message || 'Could not complete item.',
        });
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [currentUser?.userId, loadBriefing, workspaceClient]
  );

  return {
    briefing,
    filters,
    status,
    isLoading,
    refreshBriefing,
    updateBriefingFilter,
    completeBriefingTask: completeBriefingItem,
    completeBriefingItem,
  };
}
